import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import ChallengeCards from '@/components/challenges/ChallengeCards';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Trophy, Target, CheckCircle, Clock, TrendingUp, 
  Award, Sparkles, Flame 
} from 'lucide-react';
import { toast } from 'sonner';


import { BACKEND_URL } from '../../config/api';
const API = BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function Challenges({ user, onLogout }) {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    totalGpEarned: 0
  });

  useEffect(() => {
    autoCheckAndFetchChallenges();
  }, []);

  const autoCheckAndFetchChallenges = async () => {
    try {
      // First, auto-check all pending challenges
      const checkRes = await axios.post(`${API}/api/student-challenges/auto-check-all`, {}, { headers: getAuthHeaders() });
      
      if (checkRes.data.newly_completed && checkRes.data.newly_completed.length > 0) {
        const totalGP = checkRes.data.newly_completed.reduce((sum, c) => sum + c.gp_reward, 0);
        toast.success(`🎉 Completed ${checkRes.data.count} challenge(s)! +${totalGP} GP earned!`);
      }
      
      // Then fetch all challenges with updated status
      await fetchChallenges();
    } catch (error) {
      console.error('Failed to auto-check challenges:', error);
      // Still fetch challenges even if auto-check fails
      await fetchChallenges();
    }
  };

  const fetchChallenges = async () => {
    try {
      const res = await axios.get(`${API}/api/student-challenges`, { headers: getAuthHeaders() });
      setChallenges(res.data);
      
      // Calculate stats
      const total = res.data.length;
      const completed = res.data.filter(item => item.completion.status === 'completed').length;
      const inProgress = res.data.filter(item => item.completion.status === 'in_progress').length;
      const notStarted = res.data.filter(item => item.completion.status === 'not_started').length;
      const totalGpEarned = res.data.reduce((sum, item) => sum + (item.completion.gp_awarded || 0), 0);
      
      setStats({ total, completed, inProgress, notStarted, totalGpEarned });
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredChallenges = (status) => {
    return challenges.filter(item => {
      if (status === 'all') return true;
      return item.completion.status === status;
    });
  };

  const getCompletionRate = () => {
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="challenges">
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-40 bg-gray-200 rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="challenges">
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header - Removed Trophy Icon */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-navy">
            My Challenges
          </h1>
          <p className="text-[#4D4D4D] text-sm">
            Complete challenges to earn GDIOT Points! Auto-completes when you meet the requirements 🚀
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-0 card-shadow bg-[#F7F7F7] rounded-xl">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-1 text-primary-blue" strokeWidth={2} />
              <div className="text-2xl font-bold text-[#0F1C2E]">{stats.total}</div>
              <div className="text-xs text-[#4D4D4D]">Total</div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-[#F7F7F7] rounded-xl">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-1 text-[#4CAF50]" strokeWidth={2} />
              <div className="text-2xl font-bold text-[#0F1C2E]">{stats.completed}</div>
              <div className="text-xs text-[#4D4D4D]">Completed</div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-[#F7F7F7] rounded-xl">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-1 text-[#FF8B3D]" strokeWidth={2} />
              <div className="text-2xl font-bold text-[#0F1C2E]">{stats.inProgress}</div>
              <div className="text-xs text-[#4D4D4D]">In Progress</div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-[#F7F7F7] rounded-xl">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-1 text-primary-blue" strokeWidth={2} />
              <div className="text-2xl font-bold text-[#0F1C2E]">{getCompletionRate()}%</div>
              <div className="text-xs text-[#4D4D4D]">Success</div>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow bg-[#F7F7F7] rounded-xl">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 mx-auto mb-1 text-[#FF8B3D]" strokeWidth={2} />
              <div className="text-2xl font-bold text-[#0F1C2E]">{stats.totalGpEarned}</div>
              <div className="text-xs text-[#4D4D4D]">GP Earned</div>
            </CardContent>
          </Card>
        </div>

        {/* Completion Progress */}
        {/* Challenges Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-[#F7F7F7] p-1 rounded-xl mb-6">
            <TabsTrigger value="all" className="rounded-xl text-sm">
              All ({stats.total})
            </TabsTrigger>
            <TabsTrigger value="not_started" className="rounded-xl text-sm">
              Available ({stats.notStarted})
            </TabsTrigger>
            <TabsTrigger value="in_progress" className="rounded-xl text-sm">
              In Progress ({stats.inProgress})
            </TabsTrigger>
            <TabsTrigger value="completed" className="rounded-xl text-sm">
              Completed ({stats.completed})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <ChallengeCards 
              challenges={getFilteredChallenges('all')} 
              onChallengeUpdate={fetchChallenges}
              showAll={true}
            />
          </TabsContent>

          <TabsContent value="not_started" className="mt-6">
            <div className="space-y-4">
              {getFilteredChallenges('not_started').length > 0 ? (
                <>
                  <div className="bg-[#F7F7F7] p-4 rounded-xl border-l-4 border-primary-blue">
                    <div className="flex items-center space-x-2 text-[#0F1C2E]">
                      <Target className="w-5 h-5 text-primary-blue" strokeWidth={2} />
                      <span className="font-medium">Ready to start? Choose a challenge and begin your journey!</span>
                    </div>
                  </div>
                  <ChallengeCards 
                    challenges={getFilteredChallenges('not_started')} 
                    onChallengeUpdate={fetchChallenges}
                    showAll={true}
                  />
                </>
              ) : (
                <Card className="border-0 card-shadow bg-white rounded-2xl">
                  <CardContent className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-[#4CAF50]" strokeWidth={1.5} />
                    <h3 className="text-xl font-bold text-[#0F1C2E] mb-2">All Caught Up!</h3>
                    <p className="text-[#4D4D4D] font-light">You've started all available challenges. Great work!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="in_progress" className="mt-6">
            <div className="space-y-4">
              {getFilteredChallenges('in_progress').length > 0 ? (
                <>
                  <div className="bg-[#F7F7F7] p-4 rounded-xl border-l-4 border-[#FF8B3D]">
                    <div className="flex items-center space-x-2 text-[#0F1C2E]">
                      <Flame className="w-5 h-5 text-[#FF8B3D]" strokeWidth={2} />
                      <span className="font-medium">You're on fire! Keep pushing towards your goals!</span>
                    </div>
                  </div>
                  <ChallengeCards 
                    challenges={getFilteredChallenges('in_progress')} 
                    onChallengeUpdate={fetchChallenges}
                    showAll={true}
                  />
                </>
              ) : (
                <Card className="border-0 card-shadow bg-white rounded-2xl">
                  <CardContent className="p-12 text-center">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-[#FF8B3D]" strokeWidth={1.5} />
                    <h3 className="text-xl font-bold text-[#0F1C2E] mb-2">No Challenges in Progress</h3>
                    <p className="text-[#4D4D4D] font-light">Start a new challenge to see it here!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="completed" className="mt-6">
            <div className="space-y-4">
              {getFilteredChallenges('completed').length > 0 ? (
                <>
                  <div className="bg-[#F7F7F7] p-4 rounded-xl border-l-4 border-[#4CAF50]">
                    <div className="flex items-center space-x-2 text-[#0F1C2E]">
                      <Trophy className="w-5 h-5 text-[#4CAF50]" strokeWidth={2} />
                      <span className="font-medium">Amazing achievements! You've earned {stats.totalGpEarned} GP total!</span>
                    </div>
                  </div>
                  <ChallengeCards 
                    challenges={getFilteredChallenges('completed')} 
                    onChallengeUpdate={fetchChallenges}
                    showAll={true}
                  />
                </>
              ) : (
                <Card className="border-0 card-shadow bg-white rounded-2xl">
                  <CardContent className="p-12 text-center">
                    <Award className="w-16 h-16 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
                    <h3 className="text-xl font-bold text-[#0F1C2E] mb-2">No Completed Challenges Yet</h3>
                    <p className="text-[#4D4D4D] font-light">Start completing challenges to see them here!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Motivational Footer - Changed from purple to dark blue */}
        {challenges.length > 0 && (
          <Card className="border-0 card-shadow bg-gradient-to-r from-[#1E90FF] to-[#1874CD] rounded-2xl">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center space-x-2 text-white mb-2">
                <Sparkles className="w-5 h-5" strokeWidth={2} />
                <span className="font-bold text-lg">Keep Going!</span>
                <Sparkles className="w-5 h-5" strokeWidth={2} />
              </div>
              <p className="text-white opacity-90">
                Every challenge you complete brings you closer to mastering new skills. 
                You're building something amazing! 🌟
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}